import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import {
  blobResponseConfig,
  ensureExcelBlob,
  postMultipartFile,
} from '@/utils/blobApiUtils'
import queryString from 'query-string'
import { mapPhieuKhoFromApi } from '@/interfaces/phieuKhoCommonInterfaces'
import type { Huong, IPKCH } from '@/interfaces/phieuKhoCommonInterfaces'
import type { IPhieuXuatKhoPayload, PhieuXuatKhoId } from '@/interfaces/phieuXuatKhoInterfaces'
import { toNgayChungTuApiValue } from '@/utils/voucherTimeValidation'

const PREFIX = '/phieu-nhap-xuat-kho'

const HUONG_XUAT: Huong = 'XUAT'

function toLinePayload(x: NonNullable<IPhieuXuatKhoPayload['danhSachHangHoa']>[number]): Record<string, unknown> {
  return {
    hangHoaId: String(x.hangHoaId),
    maHangHoa: String(x.maHangHoa || '').trim(),
    tenHangHoa: String(x.tenHangHoa || '').trim(),
    soLuong: Number(x.soLuong) || 0,
    donViTinhMua: String(x.donViTinhMua || '').trim(),
    donGia: Number(x.donGia) || 0,
    ...(x.giamGia != null ? { giamGia: Math.max(0, Number(x.giamGia) || 0) } : {}),
    ...(x.ghiChu != null && String(x.ghiChu).trim() ? { ghiChu: String(x.ghiChu).trim() } : {}),
    ...(x.maLo != null && String(x.maLo).trim() ? { maLo: String(x.maLo).trim() } : {}),
    ...(x.hanSuDung != null && String(x.hanSuDung).trim() ? { hanSuDung: String(x.hanSuDung).trim() } : {}),
  }
}

function toPhieuBody(body: IPhieuXuatKhoPayload): Record<string, unknown> {
  return {
    loaiPhieu: body.loaiPhieu,
    ...(body.maPhieu != null && String(body.maPhieu).trim() ? { maPhieu: body.maPhieu.trim() } : {}),
    ...(body.ngayChungTu != null && String(body.ngayChungTu).trim()
      ? { ngayChungTu: toNgayChungTuApiValue(String(body.ngayChungTu)) }
      : {}),
    ...(body.khachHangId ? { khachHangId: body.khachHangId } : {}),
    ...(body.nhaCungCapId ? { nhaCungCapId: body.nhaCungCapId } : {}),
    ...(body.chiNhanhDoiId ? { chiNhanhDoiId: body.chiNhanhDoiId } : {}),
    ...(body.lyDo != null && String(body.lyDo).trim() ? { lyDo: body.lyDo.trim() } : {}),
    ...(body.ghiChu != null && String(body.ghiChu).trim() ? { ghiChu: body.ghiChu.trim() } : {}),
    giamGia: Math.max(0, Number(body.giamGia) || 0),
    ...(body.hoaDonId ? { hoaDonId: body.hoaDonId } : {}),
    ...(body.phieuTraId ? { phieuTraId: body.phieuTraId } : {}),
    ...(body.yeuCauSuaChuaId ? { yeuCauSuaChuaId: body.yeuCauSuaChuaId } : {}),
    ...(body.danhSachHangHoa && body.danhSachHangHoa.length > 0
      ? { danhSachHangHoa: body.danhSachHangHoa.map(toLinePayload) }
      : {}),
  }
}

export function phieuXuatKhoListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = { huong: HUONG_XUAT }
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  if (typeof q.search === 'string' && q.search.trim()) out.search = q.search.trim()
  if (typeof q.loaiPhieu === 'string' && q.loaiPhieu.trim()) out.loaiPhieu = q.loaiPhieu.trim()
  if (typeof q.trangThai === 'string' && q.trangThai.trim()) out.trangThai = q.trangThai.trim()
  if (typeof q.khachHangId === 'string' && q.khachHangId.trim()) out.khachHangId = q.khachHangId.trim()
  if (typeof q.chiNhanhId === 'string' && q.chiNhanhId.trim()) {
    out.chiNhanhId = q.chiNhanhId.trim()
  } else if (Array.isArray(q.chiNhanhId)) {
    const ids = q.chiNhanhId.map(String).filter(Boolean).join(',')
    if (ids) out.chiNhanhId = ids
  }
  if (typeof q.tuNgay === 'string' && q.tuNgay.trim()) out.tuNgay = q.tuNgay.trim()
  if (typeof q.denNgay === 'string' && q.denNgay.trim()) out.denNgay = q.denNgay.trim()
  if (typeof q.maKhachHang === 'string' && q.maKhachHang.trim()) out.maKhachHang = q.maKhachHang.trim()
  if (typeof q.tenKhachHang === 'string' && q.tenKhachHang.trim()) out.tenKhachHang = q.tenKhachHang.trim()
  if (typeof q.maHangHoa === 'string' && q.maHangHoa.trim()) out.maHangHoa = q.maHangHoa.trim()
  if (typeof q.tenHangHoa === 'string' && q.tenHangHoa.trim()) out.tenHangHoa = q.tenHangHoa.trim()
  if (typeof q.sort === 'string' && q.sort.trim()) out.sort = q.sort.trim()
  if (q.sortDir === 'asc' || q.sortDir === 'desc') out.sortDir = q.sortDir
  return out
}

export function fetchPhieuXuatKhoList(search: string) {
  const params = phieuXuatKhoListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-phieu`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapPhieuKhoFromApi(r as Record<string, unknown>)) as IPKCH[],
        paging,
        summary,
      }
    }),
  )
}

export function fetchPhieuXuatKhoDetail(id: PhieuXuatKhoId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-phieu`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapPhieuKhoFromApi(raw) }
    }),
  )
}

export type MaPhieuKhoMoiCtx = {
  hoaDonId?: string
  yeuCauSuaChuaId?: string
  datHangNCCId?: string
  phieuNhanHangId?: string
  phieuTraId?: string
  phieuKiemKeId?: string
}

export function fetchMaPhieuXuatKhoMoi(loaiPhieu?: string, ctx?: MaPhieuKhoMoiCtx) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/ma-phieu-moi`, {
      params: {
        huong: HUONG_XUAT,
        ...(loaiPhieu ? { loaiPhieu } : {}),
        ...(ctx?.hoaDonId ? { hoaDonId: ctx.hoaDonId } : {}),
        ...(ctx?.yeuCauSuaChuaId ? { yeuCauSuaChuaId: ctx.yeuCauSuaChuaId } : {}),
        ...(ctx?.phieuTraId ? { phieuTraId: ctx.phieuTraId } : {}),
        ...(ctx?.phieuKiemKeId ? { phieuKiemKeId: ctx.phieuKiemKeId } : {}),
      },
    }).then((res: unknown) => {
      const r = res as { data?: unknown } | string
      if (typeof r === 'string' && r.trim()) return { ma: r.trim() }
      const d = (r as { data?: unknown }).data
      if (typeof d === 'string' && d.trim()) return { ma: d.trim() }
      if (d && typeof d === 'object' && d !== null) {
        const o = d as Record<string, unknown>
        const s = o.maPhieu ?? o.ma
        if (s != null && String(s).trim()) return { ma: String(s).trim() }
      }
      return { ma: '' }
    }),
  )
}

export function taoPhieuXuatKho(body: IPhieuXuatKhoPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-phieu`, toPhieuBody(body)))
}

export function capNhatPhieuXuatKho(id: PhieuXuatKhoId, body: IPhieuXuatKhoPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-phieu`, toPhieuBody(body)))
}

export function xoaPhieuXuatKho(id: PhieuXuatKhoId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-phieu`))
}

export function hoanThanhPhieuXuatKho(id: PhieuXuatKhoId) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/hoan-thanh-phieu`, {}))
}

export function huyPhieuXuatKho(id: PhieuXuatKhoId) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/huy-phieu`, {}))
}

export async function xuatPhieuXuatKhoTongQuanBlob(search?: string): Promise<Blob> {
  const params = search ? phieuXuatKhoListQueryFromSearch(search) : { huong: HUONG_XUAT }
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-excel-tong-quan`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function xuatPhieuXuatKhoChiTietBlob(search?: string): Promise<Blob> {
  const params = search ? phieuXuatKhoListQueryFromSearch(search) : { huong: HUONG_XUAT }
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-excel-chi-tiet`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function taiTemplatePhieuXuatKhoBlob(): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-template-phieu-kho`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const nhapHangHoaTuExcelPhieuXuatKho = (id: string | number, file: File) =>
  apiRequest(postMultipartFile(`${PREFIX}/${id}/nhap-hang-hoa-tu-excel-phieu-kho`, file))

/** FE đã parse Excel → POST JSON `phieu-kho-nhap-danh-sach`. */
export const nhapDanhSachPhieuXuatKho = (
  rows: Array<{ maHang: string; soLuong: number; donGia?: number; giamGia?: number }>,
  opts?: { loaiPhieu?: string },
) => {
  const params = {
    huong: HUONG_XUAT,
    ...(opts?.loaiPhieu ? { loaiPhieu: opts.loaiPhieu } : {}),
  }
  return apiRequest(ApisServiceApp.post(`${PREFIX}/phieu-kho-nhap-danh-sach`, rows, { params }))
}

/** @deprecated Dùng `nhapDanhSachPhieuXuatKho`. */
export const nhapPhieuXuatKhoTuExcel = (
  file: File,
  opts?: { loaiPhieu?: string },
) => {
  const qs = queryString.stringify({
    huong: HUONG_XUAT,
    ...(opts?.loaiPhieu ? { loaiPhieu: opts.loaiPhieu } : {}),
  })
  return apiRequest(postMultipartFile(`${PREFIX}/nhap-phieu-tu-excel?${qs}`, file))
}
